import BroadcastChannel from 'broadcast-channel';
import debounce from 'lodash/debounce';
import nullthrows from 'nullthrows';
import Raven from 'raven-js';
// Eager CSS for the LoadingScreen only — extracted from the design system's
// tile-ui.css. Just the .skeleton / .sk-* / .logo rules + the variables
// those rules reference. No giant 130KB import.
import '@/shared/components/loading-screen/LoadingScreen.styles.css';
import React, {
  CSSProperties,
  ComponentType,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  TilePacket,
  TilePacketLogEvent,
  TilePacketListenerSubscription,
  isModulePreloaded,
} from '@apptile/tile-packet-sdk';

import AppShell from '@/app/layout-shells/app-shell/AppShell';
import EmbeddedShell from '@/app/layout-shells/embedded-shell/EmbeddedShell';
import withPreferences, {
  PreferencesContextType,
} from '@/app/providers/preferences-provider/withPreferences';
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_CODE,
  DEFAULT_DEPENDENCIES,
} from '@/config/defaults.generated';
import withAuth, { AuthProps } from '@/features/auth/hooks/with-auth/withAuth';
import AppDetails from '@/features/dashboard/components/app-details/AppDetails';
import { getBlueprint } from '@/features/dashboard/constants/blueprints';
import { AppetizeDevices } from '@/features/editor/canvas/device-frame/device-preview/appetize/AppetizeFrame';
import * as draft from '@/features/editor/collaboration/draft/draft-client';
import * as collab from '@/features/editor/collaboration/session/collab-client';
import { devBridge } from '@/features/editor/local-connect/dev-bridge/dev-bridge';
import { subscribeOpenContext, isSwapping } from '@/features/editor/open-context/open-context';
import { getPackageJsonDependencies } from '@/features/editor/session/utils/dependencies';
import getDependenciesFromQuery from '@/features/editor/session/utils/get-dependencies-from-query';
import getFilesFromQuery from '@/features/editor/session/utils/get-files-from-query';
import { setBaseSaveId } from '@/features/editor/session/utils/save-base';
import { EditorViewProps, SendCodeHint } from '@/features/editor/shell/EditorViewProps.types';
import { createTilePacketWorkerTransport } from '@/services/run-engine-bridge/tile-packet-transports';
import LazyLoad from '@/shared/components/lazy-load/LazyLoad';
import { LoadingScreen } from '@/shared/components/loading-screen/LoadingScreen';
import { versions, DEFAULT_SDK_VERSION } from '@/shared/constants/sdk';
import { uploadAssetToCas, currentAppId } from '@/shared/utils/cas-upload/cas-upload';
import { isMobile } from '@/shared/utils/detect-platform/detect-platform';
import { resolveEntryFile } from '@/shared/utils/file-utilities/file-utilities';
import { useSelector } from '@/store/hooks';
import {
  SavedTilePacket,
  QueryParams,
  SaveStatus,
  SaveHistory,
  SaveOptions,
  SDKVersion,
  Device,
  DeviceLog,
  Platform,
  TilePacketState,
  TilePacketFile,
  TilePacketCodeFile,
  TilePacketFiles,
  TilePacketDependencies,
  TilePacketDependency,
  TilePacketDefaults,
  Annotation,
} from '@/shared/types/tile-packet.types';
import Analytics from '@/shared/utils/analytics/analytics';
import {
  filter as filterPlatformOptions,
  getSelectedPlatform,
  PlatformOption,
} from '@/shared/constants/platform-options';

const BROADCAST_CHANNEL_NAME = 'TILE_PACKET_BROADCAST_CHANNEL';

type Params = {
  id?: string;
  username?: string;
  projectName?: string;
};

type Props = AuthProps &
  PreferencesContextType & {
    tilePacket?: SavedTilePacket;
    history: {
      push: (props: { pathname: string; search: string }) => void;
    };
    match: {
      params: Params;
    };
    location: {
      search: string;
    };
    query: QueryParams;
    userAgent: string;
    isEmbedded?: boolean;
    files: TilePacketFiles;
    /** Initial dependencies for the SDK session when there's no committed
     *  TilePacket yet (i.e. fresh app from a blueprint). Falls back to
     *  DEFAULT_DEPENDENCIES if undefined. */
    initialDependencies?: TilePacketDependencies;
    defaults: TilePacketDefaults;
    viewer?: any;
  };

function makeWebPlayerURL(isLocalWebPreview: boolean) {
  // Serve local web-player through `/web-player` end-point to prevent CORS issues
  if (typeof window !== 'undefined' && isLocalWebPreview) {
    return `${window.location.origin}/web-player/%%SDK_VERSION%%`;
  }
  // Derive web player URL from the parent page's host rather than a hardcoded IP
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/preview/v2/%%SDK_VERSION%%`;
  }
  return nullthrows(process.env.TILE_PACKET_WEBPLAYER_URL) + '/v2/%%SDK_VERSION%%';
}

type InitialMainState = {
  tilePacket: TilePacket;
  selectedFile: string;
  sendCodeOnChangeEnabled: boolean;
  autosaveEnabled: boolean;
  saveHistory: SaveHistory;
  saveStatus: SaveStatus;
  isPreview: boolean;
  isLocalWebPreview: boolean;
  wasUpgraded: boolean;
  initialSdkVersion: SDKVersion;
  devicePreviewShown: boolean;
  devicePreviewPlatform: Platform;
  devicePreviewPlatformOptions: PlatformOption[];
  devices: AppetizeDevices;
  verbose: boolean;
  snackagerURL: string;
  session: TilePacketState;
};

function buildInitial(props: Props, previewRef: MutableRefObject<Window | null>): InitialMainState {
  let name = props.defaults.name;
  let description = DEFAULT_DESCRIPTION;
  let sdkVersion = DEFAULT_SDK_VERSION;
  let code: TilePacketFiles | string = props.files;
  // Without a committed TilePacket the SDK session needs an initial dep set.
  let dependencies =
    props.tilePacket && props.tilePacket.code
      ? {}
      : props.initialDependencies ?? (props.files === DEFAULT_CODE ? DEFAULT_DEPENDENCIES : {});

  if (props.tilePacket) {
    code = props.tilePacket.code || code;
    dependencies = props.tilePacket.dependencies || dependencies;
    if (props.tilePacket.manifest) {
      const { manifest } = props.tilePacket;
      name = manifest.name;
      description = manifest.description;
      sdkVersion = manifest.sdkVersion || sdkVersion;
    }
  }

  if (props.query) {
    name = props.query.name || name;
    description = props.query.description || description;
    sdkVersion =
      // @ts-ignore: "latest" is not defined in SDKVersion
      props.query.sdkVersion === 'latest'
        ? DEFAULT_SDK_VERSION
        : props.query.sdkVersion || sdkVersion;
  }

  const initialSdkVersion = sdkVersion;
  let wasUpgraded = false;

  if (!versions.hasOwnProperty(sdkVersion)) {
    sdkVersion = DEFAULT_SDK_VERSION;
    wasUpgraded = true;
  }

  if (props.query.dependencies && props.query.dependencies.length) {
    dependencies = getDependenciesFromQuery(props.query.dependencies, sdkVersion);
  }

  let files: TilePacketFiles =
    typeof code === 'string' ? { 'App.js': { contents: code, type: 'CODE' } } : (code as any);

  if (typeof window !== 'undefined') {
    const { __tile_packet_embedded_session: embeddedSession } = window;
    if (embeddedSession?.files) {
      name = embeddedSession.name || name;
      description = embeddedSession.description || description;
      files = embeddedSession.files;
      dependencies = embeddedSession.dependencies || dependencies;
      sdkVersion = embeddedSession.sdkVersion || sdkVersion;
    }
  }

  // Package.json-first: the package.json FILE is the source of truth for the
  // dependency set. Parse it (getPackageJsonDependencies applies the runtime-
  // provided exclusion — react/react-native/expo/... — WITHOUT mutating the
  // file), and feed that to the SDK session. Only when there's genuinely no
  // package.json (or it's mid-edit/invalid) do we keep the seeded /
  // DEFAULT_DEPENDENCIES fallback computed above. This makes a save pushed via
  // the CLI (or edited in the browser) authoritative for what the preview
  // fetches — no separate, drifting dependency state.
  const pkgJsonFile = files['package.json'] as TilePacketCodeFile | undefined;
  if (pkgJsonFile && pkgJsonFile.type === 'CODE') {
    const parsed = getPackageJsonDependencies(pkgJsonFile, sdkVersion);
    if (parsed) {
      const derived: TilePacketDependencies = {};
      for (const [depName, depVersion] of Object.entries(parsed)) {
        derived[depName] = { version: String(depVersion) };
      }
      dependencies = derived;
    }
  }

  const isPreview = !!(
    isMobile(props.userAgent) &&
    (props.match.params.id || props.match.params.projectName) &&
    !props.isEmbedded
  );

  const id =
    !props.match.params.id && props.match.params.username && props.match.params.projectName
      ? `@${props.match.params.username}/${props.match.params.projectName}`
      : props.match.params.id && !wasUpgraded
      ? props.match.params.id
      : undefined;

  const verbose = props.query.verbose === 'true';
  const isWorker = true;
  const sendCodeOnChangeEnabled = true;
  const sessionSecret = props.getSessionSecret();
  const snackagerURL =
    typeof window !== 'undefined'
      ? `${window.location.origin}/bundler`
      : nullthrows(process.env.IMPORT_SERVER_URL);
  const isLocalWebPreview = false;

  const tilePacket = new TilePacket({
    disabled: true,
    // Empty string would crash createChannel (needs ≥6 chars). Coerce to
    // undefined so the SDK auto-generates a 10-char nanoid in that case.
    channel: props.defaults.channel || undefined,
    name,
    description,
    files,
    dependencies,
    sdkVersion,
    verbose,
    codeChangesDelay: sendCodeOnChangeEnabled ? 1000 : -1,
    createTransport: isWorker
      ? createTilePacketWorkerTransport.bind(null, props.query.testTransport || 'tile-packetpub')
      : undefined,
    reloadTimeout: 10000,
    id: !wasUpgraded ? id : undefined,
    user: sessionSecret ? { sessionSecret } : undefined,
    apiURL:
      typeof window !== 'undefined'
        ? window.location.origin
        : nullthrows(process.env.API_SERVER_URL),
    snackpubURL:
      typeof window !== 'undefined'
        ? `${window.location.origin}/pubsub`
        : process.env.TILE_PACKETPUB_URL,
    snackagerURL,
    runtimeEndpoint: nullthrows(process.env.TILE_PACKET_RUNTIME_ENDPOINT),
    webPreviewRef: typeof window !== 'undefined' ? previewRef : undefined,
    webPlayerURL: makeWebPlayerURL(isLocalWebPreview),
    // Serve binary ASSET files to the web preview as a short backend URL
    // instead of letting the SDK inline base64 into s3url — a ~500KB base64
    // "URL" is rejected by the HTTP/2 gateway (ERR_HTTP2_PROTOCOL_ERROR). The
    // backend decodes the stored base64 on read, so this is preview-only: the
    // file's contents stay base64 (autosave + builds keep the real bytes).
    // See docs/ASSETS-IN-PREVIEW.md.
    assetUrlResolver: (path: string) => {
      const appId =
        (props.query && (props.query as any).appId) ||
        (typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search).get('appId')
          : null);
      if (!appId || appId === 'default') return undefined; // scratch app → keep base64
      return `/api/apps/${encodeURIComponent(appId)}/~asset/${encodeURIComponent(path)}`;
    },
    tilePacketId: props.tilePacket?.id,
    accountTilePacketId: props.tilePacket?.accountTilePacketId,
  });

  const devicePreviewPlatformOptions = filterPlatformOptions({
    sdkVersion,
    supportedPlatformsQueryParam: props.query.supportedPlatforms,
  });
  const devicePreviewShown = props.query.preview
    ? props.query.preview !== 'false'
    : props.isEmbedded
    ? false
    : props.preferences.devicePreviewShown;
  const devicePreviewPlatform = getSelectedPlatform({
    options: devicePreviewPlatformOptions,
    sdkVersion,
    requestedPlatform:
      props.query.platform ||
      (props.isEmbedded ? 'web' : props.preferences.devicePreviewPlatform) ||
      'web',
  });

  const devices: AppetizeDevices = {
    showFrame: props.query.deviceFrame !== 'false',
    android: {
      device: props.query.deviceAndroid,
      scale: props.query.deviceAndroidScale,
    },
    ios: {
      device: props.query.deviceIos,
      scale: props.query.deviceIosScale,
    },
  };

  // Default open file = the app's entry point. Deliberately NO "else open the
  // first file": Object.keys(files)[0] is alphabetical, so it opened app.json
  // or an asset. Empty string → the editor shows its "Select a file to edit"
  // state, which is honest rather than arbitrary.
  const selectedFile = resolveEntryFile(files as any) ?? '';

  return {
    tilePacket,
    selectedFile,
    sendCodeOnChangeEnabled,
    autosaveEnabled: !props.isEmbedded,
    saveHistory: props.tilePacket?.history || [],
    saveStatus: props.tilePacket?.isDraft ? 'saved-draft' : id ? 'published' : 'unsaved',
    isPreview,
    isLocalWebPreview,
    wasUpgraded,
    initialSdkVersion,
    devicePreviewShown,
    devicePreviewPlatform,
    devicePreviewPlatformOptions,
    devices,
    verbose,
    snackagerURL,
    session: tilePacket.getState(),
  };
}

function Main(props: Props) {
  const previewRef = useRef<Window | null>(null);

  // Initialize TilePacket session + initial state lazily. Strict-mode in
  // dev WILL run this twice — that's a known cost we accept (one extra
  // SDK construction during dev).
  const initialRef = useRef<InitialMainState | null>(null);
  if (initialRef.current === null) {
    initialRef.current = buildInitial(props, previewRef);
  }
  const initial = initialRef.current;

  const tilePacket = initial.tilePacket;

  // ── core state ─────────────────────────────────────────────────────
  const [session, setSession] = useState<TilePacketState>(initial.session);
  const [selectedFile, setSelectedFile] = useState<string>(initial.selectedFile);
  const [sendCodeOnChangeEnabled, setSendCodeOnChangeEnabled] = useState(
    initial.sendCodeOnChangeEnabled,
  );
  const [autosaveEnabled, setAutosaveEnabled] = useState(initial.autosaveEnabled);
  const [isSavedOnce, setIsSavedOnce] = useState(false);
  const [saveHistory, setSaveHistory] = useState<SaveHistory>(initial.saveHistory);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>(initial.saveStatus);
  const [connectedDevices, setConnectedDevices] = useState<Device[]>([]);
  const [deviceLogs, setDeviceLogs] = useState<DeviceLog[]>([]);
  const [isPreview, setIsPreview] = useState(initial.isPreview);
  const [isLocalWebPreview, setIsLocalWebPreview] = useState(initial.isLocalWebPreview);
  const [isDownloading, setIsDownloading] = useState(false);
  const [devicePreviewShown, setDevicePreviewShown] = useState(initial.devicePreviewShown);
  const [devicePreviewPlatform, setDevicePreviewPlatform] = useState<Platform>(
    initial.devicePreviewPlatform,
  );
  const [annotations, setAnnotations] = useState<Annotation[]>([]);

  const wasUpgraded = initial.wasUpgraded;
  const initialSdkVersion = initial.initialSdkVersion;
  const devicePreviewPlatformOptions = initial.devicePreviewPlatformOptions;
  const devices = initial.devices;
  const verbose = initial.verbose;
  const snackagerURL = initial.snackagerURL;

  // ── instance refs ─────────────────────────────────────────────────
  const editedRef = useRef(false);
  const tilePacketStateListenerRef = useRef<TilePacketListenerSubscription | undefined>(undefined);
  const tilePacketLogListenerRef = useRef<TilePacketListenerSubscription | undefined>(undefined);
  const broadcastChannelRef = useRef<BroadcastChannel | undefined>(undefined);
  const isFocusedRef = useRef(false);
  const focusTimerRef = useRef<number | undefined>(undefined);
  const autoSaveTimerRef = useRef<any>(null);

  // Latest-value refs for handlers that must read fresh state without
  // re-binding listeners.
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const autosaveEnabledRef = useRef(autosaveEnabled);
  autosaveEnabledRef.current = autosaveEnabled;
  const isSavedOnceRef = useRef(isSavedOnce);
  isSavedOnceRef.current = isSavedOnce;
  const saveStatusRef = useRef(saveStatus);
  saveStatusRef.current = saveStatus;
  const propsRef = useRef(props);
  propsRef.current = props;

  // ── derived: webPreviewURL ────────────────────────────────────────
  let webPreviewURL = session.webPreviewURL;
  if (typeof window !== 'undefined') {
    let experienceURL = session.url;
    if (props.query.testTransport) {
      experienceURL += `?testTransport=${props.query.testTransport}`;
    }
    if (isLocalWebPreview) {
      webPreviewURL = `${
        window.location.origin
      }/web-player/localhost/index.html?initialUrl=${encodeURIComponent(
        experienceURL,
      )}&origin=${encodeURIComponent(window.location.origin)}&verbose=true`;
    }
  }

  // ── handlers ──────────────────────────────────────────────────────
  const handleFocusChangeInterval = useCallback(() => {
    const isFocused = document.hasFocus();
    if (isFocusedRef.current !== isFocused) {
      isFocusedRef.current = isFocused;
      if (isFocused) tilePacket.setFocus();
    }
  }, [tilePacket]);

  const enablePubNubIfNeeded = useCallback(() => {
    if (
      !propsRef.current.isEmbedded ||
      (devicePreviewShown && devicePreviewPlatform === 'mydevice')
    ) {
      tilePacket.setOnline(true);
    }
  }, [devicePreviewShown, devicePreviewPlatform, tilePacket]);

  const handleBroadcastChannelMessage = useCallback((e: any) => {
    const { id } = sessionRef.current;
    if (e.id !== id || !e.id) return;

    switch (e.type) {
      case 'NEW_TAB': {
        let nextAutosaveEnabled: boolean;
        if (isSavedOnceRef.current) {
          nextAutosaveEnabled = autosaveEnabledRef.current;
        } else {
          setAutosaveEnabled(false);
          nextAutosaveEnabled = false;
        }
        broadcastChannelRef.current?.postMessage({
          type: 'DUPLICATE_TAB',
          id,
          autosaveEnabled: nextAutosaveEnabled,
        });
        break;
      }
      case 'DUPLICATE_TAB':
        if (e.autosaveEnabled) setAutosaveEnabled(false);
        break;
    }
  }, []);

  const handleUnload = useCallback(async () => {
    if (navigator.sendBeacon && sessionRef.current.sendBeaconCloseRequest) {
      const { url, data } = sessionRef.current.sendBeaconCloseRequest;
      navigator.sendBeacon(url, data);
    }
  }, []);

  // saveAsync needs to be declared before handleSessionStateChange uses
  // it via saveDraftIfNeeded — define both as useCallbacks tied to a
  // mutable ref to break the chicken-and-egg.
  const saveAsyncRef = useRef<(opts?: SaveOptions) => Promise<void>>(async () => {});

  const saveDraftIfNeeded = useCallback((debounced?: boolean) => {
    if (
      sessionRef.current.user &&
      sessionRef.current.unsaved &&
      autosaveEnabledRef.current &&
      saveStatusRef.current === 'edited'
    ) {
      if (debounced) {
        saveDraftIfNeededDebouncedRef.current();
      } else {
        saveAsyncRef.current({ isDraft: true });
      }
    }
  }, []);
  const saveDraftIfNeededRef = useRef(saveDraftIfNeeded);
  saveDraftIfNeededRef.current = saveDraftIfNeeded;
  const saveDraftIfNeededDebouncedRef = useRef(
    debounce(() => saveDraftIfNeededRef.current(), 3000),
  );

  const debouncedLocalStorageSaveRef = useRef<() => void>(() => {});
  useEffect(() => {
    debouncedLocalStorageSaveRef.current = () => {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = setTimeout(() => {
        try {
          const files = sessionRef.current?.files;
          if (files) {
            // No localStorage — server saves only (via commit API)
          }
        } catch {
          // localStorage full or unavailable
        }
      }, 2000);
    };
  }, []);

  const handleSessionStateChange = useCallback(
    (state: TilePacketState, prevState: TilePacketState) => {
      const { connectedClients } = state;
      let nextAnnotations: Annotation[] | undefined;
      let buildNextDevices: ((prev: Device[]) => Device[]) | undefined;
      // A client that just appeared (a new key) but has no DOM tree yet is either
      // a first connect or — more importantly here — a reconnect. The runtime only
      // pushes DOM_DATA after a real code reload, so on a no-op reconnect (identical
      // files) it would never re-publish and the Layers panel / Convert-to-Tile
      // wizard would stay blank. Detect that case and explicitly re-request the
      // DOM tree from the runtime below.
      let needsDomDataRequest = false;

      if (state.connectedClients !== prevState.connectedClients) {
        for (const key in connectedClients) {
          if (!prevState.connectedClients[key] && !connectedClients[key].domData) {
            needsDomDataRequest = true;
          }
        }
      }

      if (state.connectedClients !== prevState.connectedClients) {
        buildNextDevices = (prevDevices: Device[]) => {
          const nextDevices: Device[] = [];
          for (const key in connectedClients) {
            const { id, name, platform, domData, nodeToSelect } = connectedClients[key];
            // On a transient disconnect/reconnect (e.g. a web-player iframe that
            // momentarily lost its connection) the SDK re-adds the client without
            // its cached DOM tree. We re-request DOM data on connect (see
            // needsDomDataRequest below), but until that round-trip lands we keep
            // the last-known tree for the same device instead of blanking it — that
            // prevents the Layers panel / Convert-to-Tile wizard from flashing
            // "No component tree available" across the reconnect.
            const previous = prevDevices.find((d) => d.id === id);
            nextDevices.push({
              name,
              id,
              platform: platform as any,
              status: 'connected',
              timestamp: 0,
              domData: domData || previous?.domData || { root: null },
              nodeToSelect,
            });
          }
          return nextDevices;
        };
      }

      if (state.connectedClients !== prevState.connectedClients) {
        nextAnnotations = [];
        for (const key in connectedClients) {
          const { error, transport } = connectedClients[key];
          if (error) {
            nextAnnotations.push({
              message: error.message,
              location: error.fileName
                ? {
                    fileName: error.fileName,
                    startLineNumber: error.lineNumber || 0,
                    endLineNumber: error.lineNumber || 0,
                    startColumn: (error.columnNumber || 0) + 1,
                    endColumn: (error.columnNumber || 0) + 1,
                  }
                : undefined,
              severity: 4,
              source: transport === 'web-player' ? 'Web' : 'Device',
            });
          }
        }
      }

      setSession(state);
      setSaveStatus((prev) =>
        state.unsaved && (prev === 'saved-draft' || prev === 'published' || prev === 'unsaved')
          ? editedRef.current
            ? 'edited'
            : 'unsaved'
          : prev,
      );
      if (nextAnnotations) setAnnotations(nextAnnotations);
      if (buildNextDevices) setConnectedDevices(buildNextDevices);

      // After the new state propagates, kick off the post-update side
      // effects. Use a microtask so refs update first.
      Promise.resolve().then(() => {
        saveDraftIfNeededRef.current(true);
        debouncedLocalStorageSaveRef.current();
        // Re-request the runtime DOM tree for any freshly (re)connected client
        // that came back without one. requestDOMData() resolves once DOM_DATA
        // arrives; we ignore the result (state listener applies it) and swallow
        // the timeout rejection — a slow/absent runtime just leaves the cached
        // tree in place rather than erroring.
        if (needsDomDataRequest) {
          tilePacket.requestDOMData().catch(() => {});
        }
      });
    },
    [tilePacket],
  );

  const handleSessionLog = useCallback((event: TilePacketLogEvent) => {
    const deviceLog: DeviceLog = {
      device: {
        name: (event.connectedClient && event.connectedClient.name) || '',
        id: (event.connectedClient && event.connectedClient.id) || '',
        platform: ((event.connectedClient && event.connectedClient.platform) || '') as any,
        status: 'connected',
        timestamp: Date.now(),
      },
      method: event.type as any,
      payload: [event.message],
    };
    setDeviceLogs((prev) => [...prev.slice(-99), deviceLog]);
  }, []);

  const handleDeviceConnectionAttempt = useCallback(() => {
    tilePacket.setOnline(true);
  }, [tilePacket]);

  const handleToggleSendCode = useCallback(() => {
    setSendCodeOnChangeEnabled((prev) => {
      tilePacket.setCodeChangesDelay(prev ? -1 : 1000);
      return !prev;
    });
  }, [tilePacket]);

  const handleSendCode = useCallback(
    (hint?: SendCodeHint) => {
      tilePacket.sendCodeChanges(hint);
    },
    [tilePacket],
  );

  const handleSetInspectorMode = useCallback(
    (enabled: boolean) => {
      tilePacket.setInspectorMode(enabled);
    },
    [tilePacket],
  );

  const reloadTilePacket = useCallback(() => tilePacket.reloadConnectedClients(), [tilePacket]);

  const navigateToScreen = useCallback(
    (screen: string, params?: Record<string, unknown>) => {
      tilePacket.navigate(screen, params);
    },
    [tilePacket],
  );

  const handleSubmitMetadata = useCallback(
    (details: { name: string; description: string }) => {
      editedRef.current = true;
      tilePacket.setName(details.name);
      tilePacket.setDescription(details.description);
    },
    [tilePacket],
  );

  const handleChangeSDKVersion = useCallback(
    (sdkVersion: SDKVersion, isLocalWebPreviewArg?: boolean) => {
      editedRef.current = true;
      tilePacket.setSDKVersion(sdkVersion);
      if (isLocalWebPreview !== !!isLocalWebPreviewArg) {
        const webPlayerURL = makeWebPlayerURL(!!isLocalWebPreviewArg);
        tilePacket.setWebPlayerURL(webPlayerURL);
        setIsLocalWebPreview(!!isLocalWebPreviewArg);
      }
    },
    [tilePacket, isLocalWebPreview],
  );

  const handleClearDeviceLogs = useCallback(() => setDeviceLogs([]), []);

  const saveAsync = useCallback(
    async (options: SaveOptions = {}) => {
      const { isDraft, ignoreUser, excludeFromHistory } = options;
      setSaveStatus(isDraft || excludeFromHistory ? 'saving-draft' : 'publishing');

      try {
        editedRef.current = false;
        const saveResult = await tilePacket.saveAsync({ isDraft, ignoreUser });

        Analytics.getInstance().updateCommonData({
          tilePacketId:
            saveResult.tilePacketId ||
            (propsRef.current.tilePacket && propsRef.current.tilePacket.id),
          accountTilePacketId: saveResult.accountTilePacketId,
        });

        setSession((prev) => ({
          ...prev,
          tilePacketId: saveResult.tilePacketId,
          accountTilePacketId: saveResult.accountTilePacketId,
        }));
        setIsSavedOnce(true);
        if (!excludeFromHistory) {
          setSaveHistory((prev) => [
            {
              hashId: saveResult.hashId || '',
              savedAt: new Date().toISOString(),
              isDraft,
            },
            ...prev,
          ]);
        }
        setSaveStatus(
          sessionRef.current.unsaved
            ? editedRef.current
              ? 'edited'
              : 'unsaved'
            : isDraft
            ? 'saved-draft'
            : 'published',
        );
      } catch (e) {
        editedRef.current = true;
        setSaveStatus('edited');
        throw e;
      }
    },
    [tilePacket],
  );
  saveAsyncRef.current = saveAsync;

  const handleDownloadAsync = useCallback(async () => {
    setIsDownloading(true);

    if (saveStatusRef.current !== 'published') {
      await saveAsync({ ignoreUser: true, excludeFromHistory: true });
    }

    const id = sessionRef.current.id;
    if (!id) {
      setIsDownloading(false);
      return;
    }
    Analytics.getInstance().logEvent('DOWNLOADED_CODE');

    const url = `${process.env.API_SERVER_URL}/--/api/v2/tile-packet/download/${id}`;
    const element = document.createElement('a');
    if (element && document.body) {
      document.body.appendChild(element);
      element.setAttribute('href', url);
      element.setAttribute('download', 'tile-packet.zip');
      element.style.display = '';
      element.click();
      document.body.removeChild(element);
    }
    setIsDownloading(false);
  }, [saveAsync]);

  const handleOpenEditor = useCallback(() => setIsPreview(false), []);

  // FileList imports go to the content-addressed store (Phase 2b) — the SDK's
  // legacy `/--/api/v2/snack/uploadAsset` path 404s in our stack. Returns the
  // same-origin CAS URL. Falls back to the legacy uploader if CAS is off/errors
  // or there's no app context (scratch app). See docs/ASSETS-AND-CAS.md.
  const uploadAssetAsync = useCallback(
    async (asset: File) => {
      const appId = currentAppId(props.query as any);
      if (appId && appId !== 'default') {
        try {
          return await uploadAssetToCas(appId, asset);
        } catch {
          /* CAS disabled/unreachable → fall back to the legacy SDK uploader */
        }
      }
      return tilePacket.uploadAssetAsync(asset);
    },
    [tilePacket, props.query],
  );

  const handleTogglePreview = useCallback(() => {
    if (!propsRef.current.isEmbedded) {
      propsRef.current.setPreferences({ devicePreviewShown: !devicePreviewShown });
    }
    setDevicePreviewShown((prev) => !prev);
    // enablePubNubIfNeeded reads the current devicePreviewShown via the
    // closure — schedule it after the state flush.
    setTimeout(() => enablePubNubIfNeeded(), 0);
  }, [devicePreviewShown, enablePubNubIfNeeded]);

  const handleChangePreviewPlatform = useCallback(
    (platform: Platform) => {
      if (!propsRef.current.isEmbedded) {
        propsRef.current.setPreferences({ devicePreviewPlatform: platform });
      }
      setDevicePreviewPlatform(platform);
      setTimeout(() => enablePubNubIfNeeded(), 0);
      if (platform === 'mydevice') {
        window.history.pushState({}, '', '/');
      } else {
        window.history.pushState({}, '', `?platform=${platform}`);
      }
    },
    [enablePubNubIfNeeded],
  );

  const handleSelectFile = useCallback((path: string) => {
    setSelectedFile((prev) => (prev !== path ? path : prev));
  }, []);

  const updateFiles = useCallback(
    (updateFn: (files: TilePacketFiles) => { [path: string]: TilePacketFile | null }) => {
      const state = tilePacket.getState();
      const filesUpdate = updateFn(state.files);
      if (Object.keys(filesUpdate).length) {
        editedRef.current = true;
        tilePacket.updateFiles(filesUpdate);
        // Dev Mode (Phase 2) write-back: when connected to a local `tile dev`
        // server, stream genuine browser-origin edits (design panel, Monaco, AI)
        // to disk so the developer's IDE stays in sync. devBridge.shouldWriteBack
        // suppresses echoes of files we just received FROM the server (the
        // ConnectPanel SSE handler records each incoming file's hash via
        // noteIncoming), so a streamed edit doesn't bounce back to disk. See
        // EditorV2/devBridge.ts + docs/DEV-MODE.md.
        if (devBridge.writeUrl) {
          for (const [filePath, file] of Object.entries(filesUpdate)) {
            if (file && typeof (file as any).contents === 'string') {
              const contents = (file as any).contents as string;
              if (devBridge.shouldWriteBack(filePath, contents)) {
                devBridge.write(
                  filePath,
                  contents,
                  (file as any).type === 'ASSET' ? 'ASSET' : 'CODE',
                );
              }
            }
          }
        }
        // Live co-editing: stream genuine local edits to the other editor(s) in
        // the session. collab.shouldEmit uses the same echo-guard idea as
        // devBridge — a file we just APPLIED from a peer has a matching hash, so
        // it isn't bounced back. Only runs while a live session is active.
        if (collab.isLive()) {
          for (const [filePath, file] of Object.entries(filesUpdate)) {
            // ASSET files stream as their POINTER (a tiny hosted URL), not bytes,
            // so an asset added mid-session shows up for peers. Only hosted
            // pointers ride (a relative `/…` path or an http(s) URL) — base64 /
            // blob binaries are skipped: they'd blow the tunnel's ~1MB message
            // cap AND wouldn't resolve on a peer anyway. No lock (an asset isn't
            // a single-writer code file). Deletions (file === null) fall through
            // to the code path below and stream as a generic delete.
            if (file && (file as any).type === 'ASSET') {
              const c = (file as any).contents;
              if (typeof c === 'string' && (c.startsWith('/') || /^https?:\/\//.test(c))) {
                if (collab.shouldEmit(filePath, c)) collab.emitEdit(filePath, c, 'ASSET');
              }
              continue;
            }
            const contents =
              file && typeof (file as any).contents === 'string'
                ? ((file as any).contents as string)
                : null;
            const signature = contents == null ? collab.DELETE_SIG : contents;
            if (collab.shouldEmit(filePath, signature)) {
              collab.emitEdit(filePath, contents, (file as any)?.type || 'CODE');
              collab.claimLock(filePath); // single-writer: I'm the writer of this file
            }
          }
        }
        // Draft (Tier-0 durability): record genuine buffer edits into the local
        // draft snapshot so unsaved work survives reload/crash/tab-close. No-ops
        // during a save load (the openContext host wraps replaceFiles in
        // draft.beginLoad/endLoad). CODE → note() (contents); ASSET → noteAsset()
        // (its CAS URL pointer, by-reference) so an AI/manual asset also survives
        // a reload (CAS Phase 6). noteAsset skips non-hosted (disk/base64) values.
        for (const [filePath, file] of Object.entries(filesUpdate)) {
          const contents =
            file && typeof (file as any).contents === 'string'
              ? ((file as any).contents as string)
              : null;
          // A deletion (file === null) carries no type, so fall back to the
          // pre-update buffer to tell whether the path WAS an asset — so an
          // asset deletion is tombstoned via noteAsset, not misrouted to note.
          const wasAsset = (state.files[filePath] as any)?.type === 'ASSET';
          const isAsset = file ? (file as any).type === 'ASSET' : wasAsset;
          if (isAsset) {
            draft.noteAsset(filePath, contents);
          } else {
            draft.note(filePath, contents);
          }
        }
      }
    },
    [tilePacket],
  );

  // Live co-editing: apply a peer's streamed edit through the SAME updateFiles
  // path (so buffer + preview + echo-guard all behave identically to a local
  // edit). collabClient records the applied hash first, so this does not bounce.
  useEffect(() => {
    const applyOverlay = (updates: any) =>
      updateFiles(() => updates as { [path: string]: TilePacketFile | null });
    // Read the current buffer as {type, contents} code files (ASSET excluded —
    // binaries bloat past the tunnel's ~1MB cap and aren't part of code sync).
    // Shared by collab (SYNC_REQ/reconcile) and draft (base hash + resolveOnOpen).
    const readCodeFiles = () => {
      const raw = tilePacket.getState().files as Record<string, any>;
      const out: Record<string, { type: string; contents: string }> = {};
      for (const [p, f] of Object.entries(raw)) {
        if (f && f.type !== 'ASSET' && typeof f.contents === 'string') {
          out[p] = { type: f.type || 'CODE', contents: f.contents };
        }
      }
      return out;
    };
    // Read the buffer's ASSET pointer entries so snapshots can carry them.
    // Separate from readCodeFiles (code-only, shared with draft) so assets ride
    // the collab wire without touching draft reads. Only HOSTED pointers (a
    // relative `/…` path or an http(s) URL) are sent — base64/blob contents are
    // skipped so a large inlined binary can't blow the tunnel's ~1MB cap (and
    // an un-hosted asset wouldn't resolve on the puller anyway).
    const readAssetFiles = () => {
      const raw = tilePacket.getState().files as Record<string, any>;
      const out: Record<string, { type: string; contents: string }> = {};
      const dbg: { path: string; contentsType: string; sample: string }[] = [];
      for (const [p, f] of Object.entries(raw)) {
        if (f && f.type === 'ASSET') {
          const c = f.contents;
          dbg.push({
            path: p,
            contentsType: typeof c,
            sample: typeof c === 'string' ? c.slice(0, 100) : Object.prototype.toString.call(c),
          });
          if (typeof c === 'string' && (c.startsWith('/') || /^https?:\/\//.test(c))) {
            out[p] = { type: 'ASSET', contents: c };
          }
        }
      }
      // eslint-disable-next-line no-console
      console.log(
        '[collab][readAssetFiles] ASSET entries in buffer:',
        dbg,
        '→ sending:',
        Object.keys(out),
      );
      return out;
    };
    collab.setApply(applyOverlay);
    collab.setGetFiles(readCodeFiles);
    collab.setGetAssets(readAssetFiles);
    // Draft (Tier-0): same buffer read/apply seams; note()/resolveOnOpen use them.
    draft.setApply(applyOverlay);
    draft.setGetFiles(readCodeFiles);
    const unbindDraft = draft.bindLifecycle();
    return () => {
      collab.setApply(null);
      collab.setGetFiles(null);
      collab.setGetAssets(null);
      draft.setApply(null);
      draft.setGetFiles(null);
      unbindDraft();
    };
  }, [updateFiles, tilePacket]);

  // Draft resume: when a save is loaded (open branch/cloud), decide clean-resume
  // vs drift-reconcile for any local draft. Keyed on the open context so it runs
  // once per open, not on every dirty/emit tick. Skips read-only releases.
  useEffect(() => {
    let lastKey = '';
    const unsub = subscribeOpenContext((ctx: any) => {
      // ctx is null on initial mount (no explicit open yet) — that's the
      // default/cloud buffer, which still needs a draft baseline + resume.
      if (ctx?.readOnly || isSwapping()) return;
      const appId = new URLSearchParams(window.location.search).get('appId') || 'default';
      const branch = ctx?.branch || null;
      const k = `${ctx?.kind || 'initial'}:${ctx?.id || ''}:${branch || 'cloud'}`;
      if (k === lastKey) return;
      lastKey = k;
      void draft.resolveOnOpen(appId, branch);
    });
    return () => unsub();
  }, []);

  // Read the current buffer on demand (stable callback) — the AI panel sends this
  // with each /chat so the agent's workspace is synced to what the user sees.
  const getFiles = useCallback(() => tilePacket.getState().files, [tilePacket]);

  const updateDependencies = useCallback(
    (
      updateFn: (dependencies: TilePacketDependencies) => {
        [path: string]: TilePacketDependency | null;
      },
    ) => {
      const state = tilePacket.getState();
      const dependenciesUpdate = updateFn(state.dependencies);
      if (Object.keys(dependenciesUpdate).length) {
        editedRef.current = true;
        tilePacket.updateDependencies(dependenciesUpdate);
      }
      return tilePacket.getState().dependencies;
    },
    [tilePacket],
  );

  // A DOM-data refresh is best-effort: the runtime pushes DOM_DATA on its own
  // after every reload, so this explicit request just nudges a resend. If the
  // runtime is slow/reloading it can exceed previewTimeout and reject with
  // "Operation timed out" — swallow it so a non-fatal refresh never surfaces as
  // an uncaught error overlay (mirrors the catch on the reconnect path above).
  const onRequestDOMData = useCallback(
    () => tilePacket.requestDOMData().catch(() => {}),
    [tilePacket],
  );
  const onHighlightNode = useCallback((node: any) => tilePacket.highlightNode(node), [tilePacket]);
  const onNodeSelect = useCallback((nodes: any[]) => tilePacket.selectNode(nodes), [tilePacket]);

  // ── mount: run componentDidMount equivalents ──────────────────────
  useEffect(() => {
    if (verbose) {
      console.info(
        `%c INFO `,
        `background: #2196f3; color: #fff`,
        'Verbose logging is enabled, open the web-preview in a popup to view runtime logs',
      );
    }

    if (typeof window !== 'undefined' && window.__tile_packet_embedded_session) {
      window.__tile_packet_embedded_session = undefined;
    }

    if (verbose && process.env.NODE_ENV !== 'production') {
      Analytics.getInstance().verbose = true;
    }

    if (
      typeof window !== 'undefined' &&
      (window.location.host.includes('expo.io') || window.location.host.includes('expo.dev'))
    ) {
      const build_date = new Date(process.env.BUILD_TIMESTAMP || 0).toUTCString();
      Raven.setTagsContext({ build_date });
      Analytics.getInstance().identify({ build_date });
    }

    Analytics.getInstance().setCommonData({
      accountTilePacketId: sessionRef.current.accountTilePacketId,
      tilePacketId: sessionRef.current.tilePacketId || propsRef.current.tilePacket?.id,
      isEmbedded: !!propsRef.current.isEmbedded,
      previewPane: devicePreviewShown ? devicePreviewPlatform : 'hidden',
    });

    if (wasUpgraded) {
      Analytics.getInstance().logEvent('LOADED_UNSUPPORTED_VERSION', {
        requestedVersion: initialSdkVersion,
        tilePacketId: propsRef.current.match.params.id,
      });
    }

    Analytics.getInstance().logEvent('LOADED_TILE_PACKET', {
      sdkVersion: sessionRef.current.sdkVersion,
    });

    tilePacketStateListenerRef.current = tilePacket.addStateListener(handleSessionStateChange);
    tilePacketLogListenerRef.current = tilePacket.addLogListener(handleSessionLog);

    console.log('[TILE-DEBUG] Setting session disabled=false, online=true');
    console.log('[TILE-DEBUG] snackpubURL:', process.env.TILE_PACKETPUB_URL);
    console.log('[TILE-DEBUG] snackagerURL:', process.env.IMPORT_SERVER_URL);
    console.log('[TILE-DEBUG] webPlayerURL:', makeWebPlayerURL(false));
    console.log('[TILE-DEBUG] runtimeEndpoint:', process.env.TILE_PACKET_RUNTIME_ENDPOINT);
    console.log(
      '[TILE-DEBUG] session state:',
      JSON.stringify({
        online: tilePacket.getState().online,
        disabled: tilePacket.getState().disabled,
        channel: tilePacket.getState().channel,
        connectedClients: tilePacket.getState().connectedClients,
      }),
    );
    tilePacket.setDisabled(false);
    tilePacket.setOnline(true);
    console.log(
      '[TILE-DEBUG] After enable - online:',
      tilePacket.getState().online,
      'disabled:',
      tilePacket.getState().disabled,
    );

    isFocusedRef.current = document.hasFocus();
    focusTimerRef.current = window.setInterval(handleFocusChangeInterval, 500);

    broadcastChannelRef.current = new BroadcastChannel(BROADCAST_CHANNEL_NAME, {
      webWorkerSupport: false,
    });
    broadcastChannelRef.current.postMessage({
      type: 'NEW_TAB',
      id: sessionRef.current.id,
    });
    broadcastChannelRef.current.addEventListener('message', handleBroadcastChannelMessage);

    enablePubNubIfNeeded();

    window.addEventListener('unload', handleUnload);

    if (propsRef.current.query.saveToAccount === 'true' && tilePacket.getState().user) {
      saveAsync();
    }

    return () => {
      tilePacketStateListenerRef.current?.();
      tilePacketLogListenerRef.current?.();
      tilePacket.setDisabled(true);
      tilePacket.setOnline(false);
      broadcastChannelRef.current?.close();
      window.removeEventListener('unload', handleUnload);
      if (focusTimerRef.current) clearInterval(focusTimerRef.current);
      focusTimerRef.current = undefined;
    };
    // Mount-only — handlers read latest values via refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── update side-effects (componentDidUpdate equivalents) ──────────
  const prevDevicePreviewShownRef = useRef(devicePreviewShown);
  const prevDevicePreviewPlatformRef = useRef(devicePreviewPlatform);
  const prevViewerRef = useRef(props.viewer);

  useEffect(() => {
    if (
      prevDevicePreviewShownRef.current !== devicePreviewShown ||
      prevDevicePreviewPlatformRef.current !== devicePreviewPlatform
    ) {
      Analytics.getInstance().updateCommonData({
        previewPane: devicePreviewShown ? devicePreviewPlatform : 'hidden',
      });
      prevDevicePreviewShownRef.current = devicePreviewShown;
      prevDevicePreviewPlatformRef.current = devicePreviewPlatform;
    }
  }, [devicePreviewShown, devicePreviewPlatform]);

  useEffect(() => {
    if (props.viewer !== prevViewerRef.current) {
      const sessionSecret = props.getSessionSecret();
      if (sessionRef.current.user?.sessionSecret !== sessionSecret) {
        tilePacket.setUser(sessionSecret ? { sessionSecret } : undefined);
      }
      prevViewerRef.current = props.viewer;
    }
  }, [props.viewer, props, tilePacket]);

  // ── render ─────────────────────────────────────────────────────────
  const { isEmbedded } = props;

  let experienceURL = session.url;
  if (props.query.testTransport) {
    if (experienceURL.includes('?')) {
      experienceURL += `&testTransport=${props.query.testTransport}`;
    } else {
      experienceURL += `?testTransport=${props.query.testTransport}`;
    }
  }

  if (isPreview) {
    return (
      <AppDetails
        name={session.name}
        description={session.description}
        experienceURL={experienceURL}
        onOpenEditor={handleOpenEditor}
        userAgent={props.userAgent}
        onDeviceConnectionAttempt={handleDeviceConnectionAttempt}
      />
    );
  }

  let isResolving = false;
  for (const name in session.dependencies) {
    const dep = session.dependencies[name];
    if (
      !dep.handle &&
      !dep.error &&
      !isResolving &&
      !session.disabled &&
      !isModulePreloaded(name, session.sdkVersion)
    ) {
      isResolving = true;
    }
  }

  // EditorV2 is the only shipping editor.
  return (
    <LazyLoad<ComponentType<EditorViewProps>>
      load={() => import('@/features/editor/shell/EditorShell')}
    >
      {({ loaded, data: Comp }) => {
        if (loaded && Comp) {
          return (
            <Comp
              annotations={annotations}
              autosaveEnabled={autosaveEnabled}
              connectedDevices={connectedDevices}
              createdAt={props.tilePacket ? props.tilePacket.created : undefined}
              dependencies={session.dependencies}
              missingDependencies={session.missingDependencies}
              description={session.description}
              deviceLogs={deviceLogs}
              experienceURL={experienceURL}
              experienceName={session.onlineName || session.name}
              files={session.files}
              isDownloading={isDownloading}
              isLocalWebPreview={isLocalWebPreview}
              isResolving={isResolving}
              name={session.name}
              id={session.id}
              onChangeSDKVersion={handleChangeSDKVersion}
              onClearDeviceLogs={handleClearDeviceLogs}
              onDeviceConnectionAttempt={handleDeviceConnectionAttempt}
              onDownloadAsync={handleDownloadAsync}
              onPublishAsync={saveAsync}
              onReloadTilePacket={reloadTilePacket}
              onSendCode={handleSendCode}
              onSetInspectorMode={handleSetInspectorMode}
              onSubmitMetadata={handleSubmitMetadata}
              onToggleSendCode={handleToggleSendCode}
              onTogglePreview={handleTogglePreview}
              onChangePlatform={handleChangePreviewPlatform}
              onSelectFile={handleSelectFile}
              onNavigate={navigateToScreen}
              onRequestDOMData={onRequestDOMData}
              onNodeHover={onHighlightNode}
              onNodeSelect={onNodeSelect}
              platform={devicePreviewPlatform}
              platformOptions={devicePreviewPlatformOptions}
              devices={devices}
              previewRef={previewRef}
              previewShown={devicePreviewShown}
              previewURL={webPreviewURL}
              payerCode={props.query.appetizePayerCode}
              saveHistory={saveHistory}
              saveStatus={saveStatus}
              sdkVersion={session.sdkVersion}
              selectedFile={selectedFile}
              sendCodeOnChangeEnabled={sendCodeOnChangeEnabled}
              snackagerURL={snackagerURL}
              updateDependencies={updateDependencies}
              updateFiles={updateFiles}
              getFiles={getFiles}
              uploadFileAsync={uploadAssetAsync}
              userAgent={props.userAgent}
              verbose={verbose}
              upgradedFromSDKVersion={wasUpgraded ? initialSdkVersion : undefined}
              deviceAppearance={props.query.deviceAppearance}
              channel={props.defaults.channel}
            />
          );
        } else if (isEmbedded) {
          return <EmbeddedShell />;
        } else {
          return <AppShell title={session.name} previewShown={devicePreviewShown} />;
        }
      }}
    </LazyLoad>
  );
}

// useSelector + withAuth + withPreferences replace the legacy
// compose(withPreferences, connect, withAuth) chain.
function MainWithViewer(props: Omit<Props, 'viewer'>) {
  const viewer = useSelector((state: any) => state.viewer);
  return <Main {...(props as Props)} viewer={viewer} />;
}

const MainContainer = withPreferences(withAuth(MainWithViewer));

/**
 * Fetch code from a remote source (if provided) before rendering the main app
 */

type AsyncState = {
  isReady: boolean;
  files: TilePacketFiles;
  /** Deps that pair with `files` when no committed TilePacket exists.
   *  Resolved from the chosen blueprint (or `DEFAULT_DEPENDENCIES`). */
  initialDependencies: TilePacketDependencies;
  error?: Error;
};

function buildAsyncInitial(props: Props): AsyncState {
  try {
    const blueprint = getBlueprint(props.query?.blueprint);
    const fallbackFiles = blueprint.code ?? DEFAULT_CODE;
    const files = getFilesFromQuery(props.query, fallbackFiles);
    return {
      files,
      initialDependencies: blueprint.dependencies ?? DEFAULT_DEPENDENCIES,
      isReady: false,
    };
  } catch (e) {
    return {
      error: e as Error,
      files: DEFAULT_CODE,
      initialDependencies: DEFAULT_DEPENDENCIES,
      isReady: true,
    };
  }
}

export default function AsyncApp(props: Props) {
  const [state, setState] = useState<AsyncState>(() => buildAsyncInitial(props));

  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    let cancelled = false;

    const loadFilesAsync = async (filesIn: any) => {
      const MIN_LOADING_MS = 1500;
      const startTime = Date.now();
      const paths = Object.keys(filesIn);
      let nextFiles: any;
      try {
        const contents = await Promise.all(
          Object.values(filesIn).map(async (file: any, index: number) => {
            const path = paths[index];
            if (file.url) {
              try {
                const response = await fetch(file.url);
                if (!response.ok) {
                  throw new Error(`${response.status} - ${response.statusText}`);
                }
                return await response.text();
              } catch (e: any) {
                throw new Error(`We were unable to load code for file "${path}" (${e.message})`);
              }
            } else if (file.contents) {
              return file.contents;
            } else {
              throw new Error(`No code specified for file "${path}"`);
            }
          }),
        );
        nextFiles = { ...filesIn };
        paths.forEach((path, index) => {
          nextFiles[path] = { type: filesIn[path].type, contents: contents[index] };
        });
      } catch (e: any) {
        alert(e.message);
        nextFiles = { ...filesIn };
        paths.forEach((path) => {
          nextFiles[path] = { type: filesIn[path].type, contents: '' };
        });
        if (!cancelled) {
          setState((prev) => ({ ...prev, isReady: true, files: nextFiles }));
        }
        return;
      }
      const duration = Date.now() - startTime;
      setTimeout(
        () => {
          if (!cancelled) {
            setState((prev) => ({ ...prev, isReady: true, files: nextFiles }));
          }
        },
        duration < MIN_LOADING_MS ? MIN_LOADING_MS - duration : 0,
      );
    };

    const loadFromCommitApi = async () => {
      try {
        const appId = new URLSearchParams(window.location.search).get('appId') || 'default';
        console.log('[App.loadFromSaves] Starting load for appId:', appId);
        // Opt-in load profiler: localStorage['tile:loadTrace']='1'. Reports the
        // per-request breakdown (what was cached, the single slowest request the
        // whole load waits on) + phase-2 render latency. No effect otherwise.
        let trace = false;
        try {
          trace = localStorage.getItem('tile:loadTrace') === '1';
        } catch {
          /* ignore */
        }
        const tBootStart = performance.now();
        const response = await fetch(`/api/apps/${appId}/saves`, {
          credentials: 'same-origin',
        });
        console.log('[App.loadFromSaves] Saves response status:', response.status);
        if (!response.ok) {
          console.log('[App.loadFromSaves] Response not ok, keeping defaults');
          return;
        }

        const data = await response.json();
        console.log('[App.loadFromSaves] Saves data:', data);
        // Accept both `saves` (new) and legacy `commits` field for transition.
        const list: any[] = data.saves || data.commits || [];
        if (list.length === 0) {
          console.log('[App.loadFromSaves] No saves found');
          return;
        }

        // Open at what's LIVE — the `main` ref target — never a newer save that
        // isn't main (a checkpoint sits ahead of main but must not load by
        // default). Move main to the front of the walk; the rest stays
        // newest-first as a fallback so a stale/missing main still degrades to
        // the newest resolvable save (and no-ref apps behave exactly as before).
        try {
          const headRes = await fetch(`/api/apps/${appId}/refs/main`, {
            credentials: 'same-origin',
          });
          if (headRes.ok) {
            const headId = (await headRes.json())?.saveId;
            const idOf = (e: any) => (typeof e === 'string' ? e : e.saveId || e.commitId);
            const headIdx = headId ? list.findIndex((e) => idOf(e) === headId) : -1;
            if (headIdx > 0) {
              const [head] = list.splice(headIdx, 1);
              list.unshift(head);
            }
          }
        } catch {
          /* no main ref (flag off / pre-refs app) → keep newest-first */
        }

        // Walk newest → oldest looking for a save whose file contents
        // actually resolve from S3. Rows predating the 2026-05-21 S3
        // migration carry manifests but no S3 objects — landing on one of
        // those would open the editor empty and silently revert to the
        // default scaffold. Skip stale rows until we find one with real
        // content.
        let files: Record<string, any> = {};
        let chosenSaveId: string | null = null;
        for (const entry0 of list) {
          const saveId = typeof entry0 === 'string' ? entry0 : entry0.saveId || entry0.commitId;
          if (!saveId) continue;
          const manifestResponse = await fetch(`/api/apps/${appId}/saves/${saveId}`, {
            credentials: 'same-origin',
          });
          if (!manifestResponse.ok) continue;
          const manifest = await manifestResponse.json();
          if (!manifest.manifest?.files) continue;

          const fileEntries = (
            Array.isArray(manifest.manifest.files)
              ? manifest.manifest.files
              : Object.values(manifest.manifest.files)
          ) as { path: string; type?: string; hash?: string }[];

          const HASH64 = /^[0-9a-f]{64}$/;
          const fileResults = await Promise.all(
            fileEntries
              .filter((e) => e && e.path)
              .map(async (entry) => {
                try {
                  const hasHash = !!entry.hash && HASH64.test(entry.hash);
                  // ASSET → point at the immutable CAS URL; do NOT fetch bytes
                  // here. The run-engine loads the image lazily on paint (and it's
                  // browser-cached). Fetching assets as bytes over the per-save
                  // no-cache `/file?path=` route is what made boot slow.
                  if (entry.type === 'ASSET' && hasHash) {
                    const url = `/api/apps/${appId}/~asset/cas/${entry.hash}`;
                    return { path: entry.path, file: { type: 'ASSET', contents: url } as any };
                  }
                  // CODE → fetch by immutable content-hash URL so the browser
                  // disk cache serves unchanged files free on reopen/reload.
                  if (entry.type !== 'ASSET' && hasHash) {
                    const cr = await fetch(`/api/apps/${appId}/~asset/cas/${entry.hash}`, {
                      credentials: 'same-origin',
                    });
                    if (!cr.ok) return null;
                    return {
                      path: entry.path,
                      file: { type: 'CODE', contents: await cr.text() } as any,
                    };
                  }
                  // Hashless (legacy) file: path-endpoint fallback.
                  const r = await fetch(
                    `/api/apps/${appId}/saves/${saveId}/file?path=${encodeURIComponent(
                      entry.path,
                    )}`,
                    { credentials: 'same-origin' },
                  );
                  if (!r.ok) return null;
                  const contents = await r.text();
                  return {
                    path: entry.path,
                    file: { type: entry.type || 'CODE', contents } as any,
                  };
                } catch {
                  return null;
                }
              }),
          );

          const collected: Record<string, any> = {};
          for (const result of fileResults) {
            if (result) collected[result.path] = result.file;
          }
          // Manifest says there should be files but S3 returned nothing.
          // Stale row — keep walking.
          if (fileEntries.length > 0 && Object.keys(collected).length === 0) continue;
          files = collected;
          chosenSaveId = saveId;
          break;
        }

        console.log(
          '[App.loadFromCommitApi] Loaded file count:',
          Object.keys(files).length,
          'paths:',
          Object.keys(files).slice(0, 5),
        );
        if (trace) {
          const wall = Math.round(performance.now() - tBootStart);
          const reqs = (performance.getEntriesByType('resource') as PerformanceResourceTiming[])
            .filter((e) => e.name.includes(`/api/apps/${appId}/`) && e.startTime >= tBootStart)
            .map((e) => ({
              url: e.name.replace(window.location.origin, ''),
              ms: Math.round(e.duration),
              kb: Math.round((e.encodedBodySize / 1024) * 10) / 10,
              cached: e.transferSize === 0,
            }))
            .sort((a, b) => b.ms - a.ms);
          const cached = reqs.filter((r) => r.cached).length;
          const slow = reqs[0] ? `${reqs[0].ms}ms ${reqs[0].url}` : 'n/a';
          console.log(
            `[loadTrace] ready ${wall}ms · ${reqs.length} reqs · ${cached} cached · slowest ${slow}`,
          );
          console.table(reqs);
          // Phase 2: files→editor → run-engine first reacts (bundle + AST +
          // first paint). Proxy = first message the preview iframe posts.
          const tReady = performance.now();
          const onMsg = (ev: MessageEvent) => {
            const d = ev.data;
            const isObj = !!d && typeof d === 'object';
            let runtime = false;
            if (typeof d === 'string') runtime = d.includes('"type"');
            else if (isObj) runtime = typeof (d as any).type === 'string';
            if (!runtime) return;
            window.removeEventListener('message', onMsg);
            const paintMs = Math.round(performance.now() - tReady);
            console.log(`[loadTrace] first paint ${paintMs}ms after files-ready`);
          };
          window.addEventListener('message', onMsg);
          setTimeout(() => window.removeEventListener('message', onMsg), 15000);
        }
        if (Object.keys(files).length > 0) {
          console.log(
            `[App.loadFromCommitApi] Setting ${
              Object.keys(files).length
            } committed files as initial state`,
          );
          if (!cancelled) {
            setState((prev) => ({ ...prev, files: files as TilePacketFiles, isReady: true }));
            // Remember the tip we loaded from, so the next save can 409 if the
            // tip has moved (optimistic concurrency). See saveBase.ts.
            setBaseSaveId(chosenSaveId);
          }
        } else {
          console.log('[App.loadFromCommitApi] No files loaded, keeping defaults');
        }
      } catch (e) {
        console.warn('[App.loadFromCommitApi] FAILED:', e);
      }
    };

    (async () => {
      await loadFromCommitApi();
      if (cancelled) return;
      if (!stateRef.current.isReady) {
        const hasExternalUrls = Object.values(stateRef.current.files).some((file: any) => file.url);
        if (hasExternalUrls) {
          loadFilesAsync(stateRef.current.files);
        } else {
          setState((prev) => ({ ...prev, isReady: true }));
        }
      }
      if (stateRef.current.error) {
        alert(stateRef.current.error.message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (state.isReady) {
    return (
      <MainContainer
        {...(props as any)}
        files={state.files}
        initialDependencies={state.initialDependencies}
      />
    );
  }
  return (
    <div className="tile-loading-host" style={loadingHostStyle}>
      <LoadingScreen visible caption="Loading Tile" />
    </div>
  );
}

// Inline — the host has to fill the page so the absolutely-positioned
// `.skeleton` overlay covers everything behind it.
const loadingHostStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
};
